import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useMeCore } from "../../../../cores/me";
import {
  Conversation,
  Message,
} from "../../../../cores/socialChat/interfaces/conversations.interface";
import MessageRow from "../MessageRow";
import ChatMessagesLoadingOverlay from "../ChatMessagesLoadingOverlay";
import SubmitInput from "../SubmitInput";
import { useIntl } from "react-intl";
import { Attachment } from "../../../../cores/conversations/interfaces/attachment/attachment.interface";
import { useWebChatCore } from "../../../../cores/conversations";
import { ContentMessageType } from "../../../../cores/socialChat/types/whatsapp-message.types";
import { ConversationStatus } from "../../../../cores/socialChat/types/conversation-status.types";
import { useSocialChatCore } from "../../../../cores/socialChat";
import { pickBestAvatar } from "../../../../cores/socialChat/utils/avatar-url";
import ScrollButton from "../scrollButton/ScrollButton";

interface PostMessageProps {
  companyId: string;
  conversationId: string;
  recipientId: string;
  content: string;
  type: ContentMessageType;
  status: ConversationStatus;
  attachments: Attachment[];
}
interface Props {
  selected: Conversation | null;
  messages: Message[];
  getMessages: (arg0: string, arg1: string) => void;
  postMessage: (props: PostMessageProps) => Promise<void>;
  postSeenMessage?: (arg0: string, arg1: string, arg2: string) => void;
}

export default function SocialChatForm(props: Props) {
  const intl = useIntl();
  const { attachments } = useWebChatCore();
  const { conversationStatus } = useSocialChatCore();
  const { selected, messages, getMessages, postMessage, postSeenMessage } =
    props;
  const { company, socialSettings } = useMeCore();

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref !== null) {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [messages]);

  const meId = useMemo(() => {
    if (selected?.type === "facebook") return socialSettings?.facebook?.userId;
    if (selected?.type === "instagram")
      return (
        socialSettings?.instagramMensseger?.organizationId ||
        socialSettings?.instagramLogin?.organizationId
      );
    if (selected?.type === "linkedin")
      return socialSettings?.linkedIn?.organizationId;
    if (selected?.type === "linkedinBusiness")
      return socialSettings?.linkedInBusiness?.organizationId;
  }, [selected, socialSettings]);

  const contactAvatarUrl = useMemo(() => {
    const participant = selected?.participants[0];
    if (!participant) return undefined;
    const messageAvatar = messages.find(
      (message) => String(message.from.id) === String(participant.id)
    )?.from.avatarUrl;
    return pickBestAvatar(participant.avatarUrl, messageAvatar);
  }, [selected?.participants, messages]);
  const senderAvatarUrl = useMemo(() => {
    if (selected?.type === "facebook")
      return socialSettings?.facebook?.avatarUrl;
    if (selected?.type === "instagram")
      return (
        socialSettings?.instagramMensseger?.avatarUrl ||
        socialSettings?.instagramLogin?.avatarUrl
      );
    if (selected?.type === "linkedin")
      return socialSettings?.linkedIn?.avatarUrl;
    if (selected?.type === "linkedinBusiness")
      return socialSettings?.linkedInBusiness?.avatarUrl;
    return undefined;
  }, [selected?.type, socialSettings]);

  const isWhatsappCoexistence = useMemo(
    () =>
      selected?.type === "whatsapp" &&
      socialSettings?.whatsapp?.connectionType === "coexistence",
    [selected?.type, socialSettings?.whatsapp?.connectionType]
  );

  const itsMe = useCallback(
    (message: Message) => {
      if (selected?.type === "whatsapp") return !!message.from.email;
      return meId === message.from.id;
    },
    [selected, meId]
  );

  const onSubmit = async (message = "") => {
    if (attachments.length > 0) {
      await postMessage({
        companyId: company?.id!,
        conversationId: selected?.id!, //conversationId
        recipientId: selected?.participants[0]?.id!, //recipientId
        content: message,
        type: attachments[0].type,
        attachments,
        status: conversationStatus,
      });
    }

    if (message) {
      await postMessage({
        companyId: company?.id!,
        conversationId: selected?.id!, //conversationId
        recipientId: selected?.participants[0]?.id!, //recipientId
        content: message,
        type: "text",
        attachments: [],
        status: conversationStatus,
      });
    }
  };

  const disabled = useCallback(() => {
    let isDisabled = true;

    const messagesFromUser = messages.filter(
      (message) => message.from.id !== meId
    );
    if (messagesFromUser.length > 0) {
      const lastMessage =
        messagesFromUser[messagesFromUser.length - 1].timestamp;
      const lastMessageTime = moment(`${lastMessage.date} ${lastMessage.time}`);
      const minutesDiff = moment().utc().diff(lastMessageTime, "minutes");

      isDisabled = minutesDiff > 24 * 60;
    }

    return false;
  }, [messages, meId]);

  useEffect(() => {
    if (!company?.id || !selected?.id) return;
    getMessages(company.id, selected.id);
  }, [getMessages, selected?.id, company?.id]);

  useEffect(() => {
    if (!disabled()) {
      postSeenMessage?.(company?.id!, selected?.id!, selected?.fromId!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, company, postSeenMessage, disabled]);
  return (
    <div className="chat-form-layout d-flex flex-column flex-grow-1 min-h-0 w-100 h-100 position-relative">
      <div className="chat-form-layout__messages min-h-0 overflow-auto backdrop-chat position-relative">
        <ChatMessagesLoadingOverlay />
        {isWhatsappCoexistence && (
          <div className="alert alert-success py-2 px-3 m-2 mb-0 small">
            {intl.formatMessage({ id: "app.whatsappConnect.inboxBanner" })}
          </div>
        )}
        {messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            itsMe={itsMe(message)}
            contactAvatarUrl={contactAvatarUrl}
            senderAvatarUrl={senderAvatarUrl}
          />
        ))}
        <div ref={ref} />
        {disabled() && (
          <div className="text-center text-muted small py-2">
            {intl.formatMessage({ id: "app.chatFacebook.notHaveMsg" })}
          </div>
        )}
        <ScrollButton reference={ref} />
      </div>
      <div className="chat-form-layout__composer">
        <SubmitInput
          attachments={attachments}
          conversation={selected}
          disableAttachments={selected?.type !== "whatsapp"}
          onSubmit={onSubmit}
          disabled={disabled()}
        />
      </div>
    </div>
  );
}
